import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Button, FormLabel, Grid, Stack, TextField } from '@mui/material';

import { object, string } from 'yup';

import Axios from '../../Assets/Axios/Axios';

import ToastContext from '../../Context/ToastContext';
import { useAuthContext } from '../../Context/AuthContext';

import { COLORS } from '../../Constant/COLORS';

import Layout from './Components/Layout';
import { TypoPoppins } from '../../Components/Typo';
import { LoadingButton, SectionLoader } from '../../Components/Loaders';
import { ErrorState } from '../../Components/ErrorStates';

import { getClearedErrorObj } from '../../Helpers';

import { FindUser } from '../../Server/Query';
import { UpdateUser } from '../../Server/Mutation';

const MyDetails = () => {
  const { user } = useAuthContext();
  const { showToastMessage } = useContext(ToastContext);

  const [inputs, setInputs] = useState({
    firstName: '',
    lastName: '',
    address: '',
    province: '',
    postalCode: '',
    phoneNumber: '',
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    address: '',
    province: '',
    postalCode: '',
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const fetchAndSetUserDetails = useCallback(async () => {
    if (!user?.userID) return undefined;
    setIsLoading(true);
    setLoadError(null);
    try {
      const query = FindUser({ userID: user.userID });
      const { data, errors } = await Axios(query);

      if (errors) {
        setLoadError("We couldn't load your details. Please try again.");
        return undefined;
      }

      if (data.EventBooker === null) {
        setLoadError("We couldn't find your profile. Please try again.");
        return undefined;
      }

      if (data.EventBooker.is_active === false) {
        setLoadError('Your account has been disabled. Please contact support.');
        return undefined;
      }

      const eventBooker = data.EventBooker;

      setInputs({
        firstName: eventBooker.first_name ?? '',
        lastName: eventBooker.last_name ?? '',
        address: eventBooker.address_line_1 ?? '',
        province: eventBooker.province ?? '',
        postalCode: eventBooker.postal_code ?? '',
        phoneNumber: eventBooker.mobile_number ?? '',
      });
    } catch (err) {
      setLoadError("We couldn't reach the server. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  }, [user?.userID]);

  useEffect(() => {
    fetchAndSetUserDetails();
  }, [fetchAndSetUserDetails]);

  function handleChange(event) {
    const { value, name } = event.target;
    setInputs(prevData => ({ ...prevData, [name]: value }));
  }

  const handleSubmit = async event => {
    event.preventDefault();
    setIsSaving(true);
    try {
      const validatedInputs = await schema.validate(inputs, {
        abortEarly: false,
      });

      setErrors(currentErrors => getClearedErrorObj(currentErrors));

      const query = UpdateUser({ id: user.userID, ...validatedInputs });
      const { errors } = await Axios(query);

      errors
        ? showToastMessage('Failed to updated', 'error')
        : showToastMessage('Details updated successfully', 'success');
    } catch (error) {
      if (error.name === 'ValidationError') {
        const [validationError] = error.inner;
        setErrors(currentErrors => ({
          ...getClearedErrorObj(currentErrors),
          [validationError.path]: validationError.message,
        }));
      }
    } finally {
      setIsSaving(false);
    }
  };

  function handleClickCancel() {
    fetchAndSetUserDetails();
  }

  if (isLoading) {
    return (
      <Layout>
        <SectionLoader height={360} />
      </Layout>
    );
  }

  if (loadError) {
    return (
      <Layout>
        <ErrorState
          title="Couldn't load your details"
          message={loadError}
          onRetry={fetchAndSetUserDetails}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <Stack
        component='form'
        direction={{ xs: 'column', md: 'row' }}
        onSubmit={handleSubmit}
        gap={2}
        px={{ xs: 1, md: 'unset' }}
        ml={{ xs: 1, md: 'unset' }}>
        <Grid
          flexGrow={1}
          container
          columns={7.5}
          maxWidth={{ xs: '100%', md: 7 / 10 }}
          rowSpacing={{ xs: 1, md: 3 }}
          columnSpacing={2}
          alignItems='center'>
          <Grid item xs={7.5} md={3} component={FormLabel}>
            <TypoPoppins fontWeight={500}>First Name</TypoPoppins>
          </Grid>
          <Grid item xs={7.5} md={4}>
            <TextField
              fullWidth
              autoComplete='given-name'
              inputProps={{ spellCheck: false }}
              name='firstName'
              value={inputs.firstName}
              onChange={handleChange}
              helperText={errors.firstName}
              error={Boolean(errors.firstName)}
            />
          </Grid>

          <Grid item xs={7.5} md={3} component={FormLabel}>
            <TypoPoppins fontWeight={500}>Last Name</TypoPoppins>
          </Grid>
          <Grid item xs={7.5} md={4}>
            <TextField
              fullWidth
              autoComplete='family-name'
              inputProps={{ spellCheck: false }}
              name='lastName'
              value={inputs.lastName}
              onChange={handleChange}
              helperText={errors.lastName}
              error={Boolean(errors.lastName)}
            />
          </Grid>

          <Grid item xs={7.5} md={3} component={FormLabel}>
            <TypoPoppins fontWeight={500}>Address</TypoPoppins>
          </Grid>
          <Grid item xs={7.5} md={4}>
            <TextField
              fullWidth
              autoComplete='address-line1'
              inputProps={{ spellCheck: false }}
              name='address'
              value={inputs.address}
              onChange={handleChange}
              helperText={errors.address}
              error={Boolean(errors.address)}
            />
          </Grid>

          <Grid item xs={7.5} md={3} component={FormLabel}>
            <TypoPoppins fontWeight={500}>Province</TypoPoppins>
          </Grid>
          <Grid item xs={7.5} md={4}>
            <TextField
              fullWidth
              autoComplete='address-level1'
              inputProps={{ spellCheck: false }}
              name='province'
              value={inputs.province}
              onChange={handleChange}
              helperText={errors.province}
              error={Boolean(errors.province)}
            />
          </Grid>

          <Grid item xs={7.5} md={3} component={FormLabel}>
            <TypoPoppins fontWeight={500}>Post Code</TypoPoppins>
          </Grid>
          <Grid item xs={7.5} md={4}>
            <TextField
              fullWidth
              autoComplete='postalCode'
              inputProps={{ spellCheck: false }}
              name='postalCode'
              value={inputs.postalCode}
              onChange={handleChange}
              helperText={errors.postalCode}
              error={Boolean(errors.postalCode)}
            />
          </Grid>

          <Grid item xs={7.5} md={3} component={FormLabel}>
            <TypoPoppins fontWeight={500}>Phone number</TypoPoppins>
          </Grid>
          <Grid item xs={7.5} md={4}>
            <TextField
              disabled
              InputProps={{ readOnly: true }}
              fullWidth
              autoComplete='tel'
              value={inputs.phoneNumber}
            />
          </Grid>
        </Grid>

        <Grid
          flexShrink={1.3}
          ml={{ xs: -1.5, md: 3 }}
          container
          columns={2}
          columnSpacing={{ xs: 1, md: 4 }}>
          <Grid item xs={1}>
            <Button
              onClick={handleClickCancel}
              size='small'
              variant='outlined'
              disabled={isSaving}
              sx={{ color: COLORS.blue, fontWeight: '500' }}
              fullWidth>
              Cancel
            </Button>
          </Grid>
          <Grid item xs={1}>
            <LoadingButton
              type='submit'
              size='small'
              color='blue'
              isLoading={isSaving}
              sx={{ color: 'white', fontWeight: '500' }}
              fullWidth>
              Save
            </LoadingButton>
          </Grid>
        </Grid>
      </Stack>
    </Layout>
  );
};

const schema = object({
  firstName: string().trim().required().label('First Name'),
  lastName: string().trim().required().label('Last Name'),
  address: string().trim().required().label('Address'),
  province: string().trim().required().label('Province'),
  postalCode: string().trim().required().label('Postal Code'),
});

export default MyDetails;
